import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import Notification, { NotificationContainer } from '../../../components/Notification';
import tprmService from '../../../services/compliance/tprm.service';
import { useComplianceAccess } from '../../../hooks/useComplianceAccess';
import { Vendor } from '../../../types/compliance';
import RemediationsPanel from './RemediationsPanel';
import RiskAcceptancesPanel from './RiskAcceptancesPanel';
import Modal from '../../../components/compliance/Modal';
import EntityAuditPanel from '../audit/EntityAuditPanel';
import EvidencePanel from './EvidencePanel';
import IncidentsPanel from './IncidentsPanel';
import OffboardingPanel from './OffboardingPanel';
import PerformanceReviewsPanel from './PerformanceReviewsPanel';
import VendorFilesPanel from './VendorFilesPanel';
import { useUserNames } from '../../../hooks/useUserNames';

const TIER_BADGE: Record<string, string> = {
  low: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  medium: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  high: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  critical: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  submitted: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  under_review: 'bg-indigo-200 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  approved: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  approved_with_conditions: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  rejected: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
  archived: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

const DOMAIN_LABELS: Record<string, string> = {
  data_sensitivity: 'Data Sensitivity',
  system_access: 'System / Access Risk',
  operational: 'Operational Criticality',
  security: 'Security Maturity',
  financial: 'Financial / Contractual',
  compliance: 'Compliance / Regulatory',
  business_context: 'Business Context',
};

const SECTION_LETTER_TO_DOMAIN: Record<string, string> = {
  b: 'data_sensitivity',
  c: 'system_access',
  d: 'operational',
  e: 'security',
  f: 'financial',
  g: 'compliance',
};

// Acronyms that should stay fully capitalized when displayed.
const HUMANIZE_ACRONYMS = new Set([
  'mfa', 'sso', 'pci', 'hipaa', 'gdpr', 'rbac', 'rto', 'idp', 'dpa', 'api',
  'tms', 'wms', 'edi', 'iot', 'sla', 'dr', 'sox', 'ccpa', 'hitrust', 'ir',
  'soc', 'iso', 'bcdr',
]);

function humanizeKey(k: string): string {
  return k
    .split('_')
    .map((word) => {
      if (HUMANIZE_ACRONYMS.has(word.toLowerCase())) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

type TabKey =
  | 'overview'
  | 'answers'
  | 'breakdown'
  | 'remediations'
  | 'risk_acceptances'
  | 'evidence'
  | 'files'
  | 'incidents'
  | 'performance'
  | 'offboarding'
  | 'audit';

function Pill({ text, className }: { text: string; className: string }) {
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${className}`}>{text}</span>
  );
}

function formatAnswer(val: unknown): string {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (Array.isArray(val)) return val.length ? val.join(', ') : '—';
  return String(val);
}

export default function VendorDetail() {
  const { id } = useParams<{ id: string }>();
  const { nameFor } = useUserNames();
  const navigate = useNavigate();
  const { isManager, hasAny } = useComplianceAccess();
  const canSubmit = hasAny;
  const canReview = isManager;
  const canApprove = isManager;
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [tab, setTab] = useState<TabKey>('overview');
  const [answersOpen, setAnswersOpen] = useState<Record<string, boolean>>({});
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);
  const [successNotification, setSuccessNotification] =
    useState<{ title: string; message: string } | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveWithConditions, setApproveWithConditions] = useState(false);
  const [approveNextDate, setApproveNextDate] = useState('');

  const todayIso = new Date().toISOString().slice(0, 10);

  const loadVendor = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const v = await tprmService.getVendor(id);
      setVendor(v);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to load vendor', message: msg });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadVendor();
  }, [loadVendor]);

  const runAction = useCallback(
    async (fn: () => Promise<unknown>, label: string) => {
      setActionBusy(true);
      try {
        await fn();
        await loadVendor();
        setSuccessNotification({ title: label, message: 'Vendor updated.' });
      } catch (error: any) {
        const detail = error?.response?.data?.detail;
        const msg =
          typeof detail === 'string' ? detail : error?.message || 'Unknown error occurred';
        setErrorNotification({ title: `Failed: ${label}`, message: msg });
      } finally {
        setActionBusy(false);
      }
    },
    [loadVendor],
  );

  const handleDelete = useCallback(async () => {
    if (!vendor) return;
    if (!window.confirm(`Delete draft vendor "${vendor.name}"? This cannot be undone.`))
      return;
    setActionBusy(true);
    try {
      await tprmService.deleteVendor(vendor.id);
      navigate('/compliance/tprm?tab=data');
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to delete', message: msg });
    } finally {
      setActionBusy(false);
    }
  }, [navigate, vendor]);

  const handleApprove = useCallback(
    (withConditions: boolean) => {
      if (!vendor) return;
      setApproveWithConditions(withConditions);
      setApproveNextDate('');
      setApproveOpen(true);
    },
    [vendor],
  );

  const confirmApprove = useCallback(() => {
    if (!vendor) return;
    const body: { with_conditions: boolean; next_review_date?: string } = {
      with_conditions: approveWithConditions,
    };
    const trimmed = approveNextDate.trim();
    if (trimmed) body.next_review_date = trimmed;
    runAction(
      () => tprmService.approve(vendor.id, body),
      approveWithConditions ? 'Approved with Conditions' : 'Approved',
    );
    setApproveOpen(false);
    setApproveNextDate('');
  }, [approveNextDate, approveWithConditions, runAction, vendor]);

  // ESC-to-close is handled by the shared Modal primitive.

  const handleReject = useCallback(() => {
    if (!vendor) return;
    if (!rejectReason.trim()) {
      setErrorNotification({
        title: 'Reason required',
        message: 'Please provide a rejection reason.',
      });
      return;
    }
    if (rejectReason.length > 2000) {
      setErrorNotification({
        title: 'Reason too long',
        message: 'Max 2000 characters.',
      });
      return;
    }
    runAction(
      () => tprmService.reject(vendor.id, { notes: rejectReason }),
      'Rejected',
    );
    setRejectOpen(false);
    setRejectReason('');
  }, [rejectReason, runAction, vendor]);

  const actionButtons = useMemo(() => {
    if (!vendor) return null;
    const s = vendor.status;
    const btn = (onClick: () => void, label: string, primary = false) => (
      <button
        key={label}
        type="button"
        disabled={actionBusy}
        onClick={onClick}
        className={`px-3 py-2 text-sm font-medium rounded-lg disabled:opacity-50 ${
          primary
            ? 'text-white bg-blue-600 hover:bg-blue-700'
            : 'border border-gray-300 dark:border-gray-600 hover:bg-highlight'
        }`}
      >
        {label}
      </button>
    );

    const buttons: React.ReactNode[] = [];

    if (s === 'draft') {
      if (canSubmit) {
        buttons.push(
          btn(() => navigate(`/compliance/tprm/vendors/${vendor.id}/edit`), 'Edit'),
        );
      }
      if (canApprove) {
        buttons.push(btn(handleDelete, 'Delete'));
      }
      if (canSubmit) {
        buttons.push(
          btn(
            () =>
              runAction(() => tprmService.submit(vendor.id), 'Submitted for Review'),
            'Submit for Review',
            true,
          ),
        );
      }
    } else if (s === 'submitted') {
      if (canReview) {
        buttons.push(
          btn(() => navigate(`/compliance/tprm/vendors/${vendor.id}/edit`), 'Edit'),
        );
        buttons.push(
          btn(
            () => runAction(() => tprmService.startReview(vendor.id), 'Review Started'),
            'Start Review',
            true,
          ),
        );
      }
    } else if (s === 'under_review') {
      if (canApprove) {
        buttons.push(btn(() => handleApprove(false), 'Approve', true));
        buttons.push(btn(() => handleApprove(true), 'Approve with Conditions'));
        buttons.push(btn(() => setRejectOpen(true), 'Reject'));
      }
    } else if (s === 'approved' || s === 'approved_with_conditions') {
      if (canReview) {
        buttons.push(
          btn(() => runAction(() => tprmService.rescore(vendor.id), 'Rescored'), 'Rescore'),
        );
      }
      if (canApprove) {
        buttons.push(
          btn(
            () =>
              runAction(() => tprmService.archiveVendor(vendor.id), 'Archived'),
            'Archive',
          ),
        );
      }
    } else if (s === 'rejected') {
      if (canApprove) {
        buttons.push(
          btn(
            () =>
              runAction(() => tprmService.archiveVendor(vendor.id), 'Archived'),
            'Archive',
          ),
        );
      }
    }

    return buttons;
  }, [actionBusy, handleApprove, handleDelete, navigate, runAction, vendor, canSubmit, canReview, canApprove]);

  const sectionScores = vendor?.scores;

  return (
    <div className="h-full w-full p-6 pb-24">
      {/* Breadcrumb */}
      <div className="text-xs text-gray-500 mb-1">
        <Link to="/compliance/tprm" className="text-blue-600 hover:underline">
          TPRM
        </Link>
        {' › '}
        <Link to="/compliance/tprm?tab=data" className="text-blue-600 hover:underline">
          Register
        </Link>
      </div>

      {loading && <div className="text-center text-gray-500 py-12">Loading vendor...</div>}

      {vendor && !loading && (
        <>
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold">{vendor.name}</h1>
                <Pill
                  text={vendor.status.replace(/_/g, ' ')}
                  className={STATUS_BADGE[vendor.status] || ''}
                />
                {vendor.scores?.risk_tier && (
                  <Pill
                    text={vendor.scores.risk_tier}
                    className={TIER_BADGE[vendor.scores.risk_tier] || ''}
                  />
                )}
                {vendor.scores?.total_score != null && (
                  <span className="text-lg font-semibold">
                    {vendor.scores.total_score.toFixed(2)}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {vendor.legal_entity_name || vendor.product_service_name || '—'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 justify-end items-start">
              {actionButtons}
              <Link
                to={`/compliance/tprm/vendors/${vendor.id}/report`}
                className="px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-highlight"
                title="View printable assessment report"
              >
                Print Report
              </Link>
            </div>
          </div>

          {vendor.status === 'rejected' && vendor.notes && (
            <div className="mb-4 p-3 border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950 rounded-lg text-sm">
              <strong className="text-red-800 dark:text-red-200">Rejection reason: </strong>
              <span className="text-red-700 dark:text-red-300">{vendor.notes}</span>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 mb-4">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'answers', label: 'Answers' },
              { id: 'breakdown', label: 'Score Breakdown' },
              { id: 'remediations', label: 'Remediations' },
              { id: 'risk_acceptances', label: 'Risk Acceptances' },
              { id: 'evidence', label: 'Evidence' },
              { id: 'files', label: 'Files' },
              { id: 'incidents', label: 'Incidents' },
              ...(vendor.status === 'approved' ||
              vendor.status === 'approved_with_conditions'
                ? [{ id: 'performance', label: 'Performance' }]
                : []),
              ...(vendor.status === 'archived'
                ? [{ id: 'offboarding', label: 'Offboarding' }]
                : []),
              { id: 'audit', label: 'Audit' },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id as TabKey)}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                  tab === t.id
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-panel">
                <h3 className="text-sm font-semibold mb-3">Identity</h3>
                <dl className="grid grid-cols-2 gap-y-2 text-sm">
                  <dt className="text-gray-500">Service Type</dt>
                  <dd>{vendor.service_type || '—'}</dd>
                  <dt className="text-gray-500">Department</dt>
                  <dd>{vendor.department || '—'}</dd>
                  <dt className="text-gray-500">Business Owner</dt>
                  <dd>{nameFor(vendor.business_owner_oid) || '—'}</dd>
                  <dt className="text-gray-500">Technical Owner</dt>
                  <dd>{nameFor(vendor.technical_owner_oid) || '—'}</dd>
                  <dt className="text-gray-500">Primary Contact</dt>
                  <dd>{vendor.primary_contact_name || '—'}</dd>
                  <dt className="text-gray-500">Go-Live</dt>
                  <dd>{vendor.requested_go_live || '—'}</dd>
                  <dt className="text-gray-500">Annual Value</dt>
                  <dd>{vendor.annual_value ? `$${vendor.annual_value}` : '—'}</dd>
                  <dt className="text-gray-500">Is Replacement</dt>
                  <dd>{vendor.is_replacement ? 'Yes' : 'No'}</dd>
                  <dt className="text-gray-500">Next Review</dt>
                  <dd>{vendor.next_review_date || '—'}</dd>
                  <dt className="text-gray-500">Created</dt>
                  <dd>{new Date(vendor.created_at).toLocaleDateString()}</dd>
                </dl>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-panel">
                <h3 className="text-sm font-semibold mb-3">Score Summary</h3>
                {sectionScores ? (
                  <>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl font-bold">
                        {sectionScores.total_score != null
                          ? sectionScores.total_score.toFixed(2)
                          : '—'}
                      </span>
                      {sectionScores.risk_tier && (
                        <Pill
                          text={sectionScores.risk_tier}
                          className={TIER_BADGE[sectionScores.risk_tier] || ''}
                        />
                      )}
                    </div>
                    {([
                      ['data_sensitivity', sectionScores.score_data_sensitivity],
                      ['system_access', sectionScores.score_system_access],
                      ['operational', sectionScores.score_operational],
                      ['security', sectionScores.score_security],
                      ['financial', sectionScores.score_financial],
                      ['compliance', sectionScores.score_compliance],
                      ['business_context', sectionScores.score_business_context],
                    ] as const).map(([k, v]) => (
                      <div key={k} className="mb-2">
                        <div className="flex justify-between text-xs mb-0.5">
                          <span>{DOMAIN_LABELS[k]}</span>
                          <span>{v != null ? v.toFixed(1) : '—'}</span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded">
                          <div
                            className="h-2 rounded bg-blue-500"
                            style={{
                              width: `${Math.min(100, Math.max(0, v ?? 0))}%`,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="text-sm text-gray-500">No scores yet.</p>
                )}
                <h4 className="text-sm font-semibold mt-4 mb-2">Flags</h4>
                <div className="flex flex-wrap gap-2">
                  {vendor.flags.has_pii && (
                    <Pill text="PII" className="bg-danger text-danger-fg" />
                  )}
                  {vendor.flags.mfa_supported && (
                    <Pill text="MFA" className="bg-ok text-ok-fg" />
                  )}
                  {vendor.flags.sso_supported && (
                    <Pill text="SSO" className="bg-ok text-ok-fg" />
                  )}
                  {vendor.flags.is_critical_operations && (
                    <Pill text="Critical Ops" className="bg-warn text-warn-fg" />
                  )}
                  {vendor.flags.handles_payment && (
                    <Pill text="Payment" className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200" />
                  )}
                  {!vendor.flags.has_pii &&
                    !vendor.flags.mfa_supported &&
                    !vendor.flags.sso_supported &&
                    !vendor.flags.is_critical_operations &&
                    !vendor.flags.handles_payment && (
                      <span className="text-xs text-gray-500">No flags set.</span>
                    )}
                </div>
              </div>
            </div>
          )}

          {tab === 'answers' && (
            <div>
              {(['b', 'c', 'd', 'e', 'f', 'g'] as const).map((letter) => {
                const answers: Record<string, any> =
                  (vendor as any)[`answers_${letter}`] || {};
                const open = !!answersOpen[letter];
                const entries = Object.entries(answers);
                return (
                  <div
                    key={letter}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg mb-2 bg-panel"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setAnswersOpen((o) => ({ ...o, [letter]: !o[letter] }))
                      }
                      className="w-full flex items-center justify-between px-4 py-3 text-left font-semibold"
                    >
                      <span>
                        {letter.toUpperCase()} — {DOMAIN_LABELS[SECTION_LETTER_TO_DOMAIN[letter]]}
                      </span>
                      {open ? (
                        <ChevronUpIcon className="w-5 h-5" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5" />
                      )}
                    </button>
                    {open && (
                      <div className="p-4 pt-0 text-sm">
                        {entries.length === 0 && (
                          <p className="text-gray-500">No answers recorded.</p>
                        )}
                        {entries.length > 0 && (
                          <dl className="grid grid-cols-2 gap-y-1">
                            {entries.map(([k, v]) => (
                              <div key={k} className="contents">
                                <dt className="text-gray-500">{humanizeKey(k)}</dt>
                                <dd>{formatAnswer(v)}</dd>
                              </div>
                            ))}
                          </dl>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {tab === 'breakdown' && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-panel">
              {Object.keys(DOMAIN_LABELS).map((domain) => {
                const sec: any = vendor.score_breakdown?.[domain];
                if (!sec) return null;
                return (
                  <details key={domain} className="mb-2">
                    <summary className="cursor-pointer text-sm font-semibold">
                      {DOMAIN_LABELS[domain]} — {sec.score?.toFixed?.(1) ?? sec.score ?? '—'}
                    </summary>
                    <div className="pl-4 mt-1 text-xs">
                      {sec.questions &&
                        Object.entries(sec.questions).map(([q, v]: [string, any]) => (
                          <div key={q} className="mb-1">
                            <strong>{q}</strong>: {formatAnswer(v?.answer)} → {v?.contribution}
                          </div>
                        ))}
                    </div>
                  </details>
                );
              })}
              {!Object.keys(vendor.score_breakdown || {}).length && (
                <p className="text-sm text-gray-500">No score breakdown available yet.</p>
              )}
            </div>
          )}

          {tab === 'remediations' && <RemediationsPanel vendorId={vendor.id} />}
          {tab === 'risk_acceptances' && <RiskAcceptancesPanel vendorId={vendor.id} />}
          {tab === 'evidence' && <EvidencePanel vendorId={vendor.id} />}
          {tab === 'files' && <VendorFilesPanel vendorId={vendor.id} />}
          {tab === 'incidents' && <IncidentsPanel vendorId={vendor.id} />}
          {tab === 'performance' && <PerformanceReviewsPanel vendorId={vendor.id} />}
          {tab === 'offboarding' && <OffboardingPanel vendorId={vendor.id} />}
          {tab === 'audit' && (
            <EntityAuditPanel entityType="vendor" entityId={vendor.id} />
          )}
        </>
      )}

      {/* Approve modal */}
      <Modal
        open={approveOpen && !!vendor}
        onClose={() => {
          setApproveOpen(false);
          setApproveNextDate('');
        }}
        title={approveWithConditions ? 'Approve with Conditions' : 'Approve Vendor'}
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setApproveOpen(false);
                setApproveNextDate('');
              }}
              className="px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmApprove}
              disabled={actionBusy}
              className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
            >
              Approve
            </button>
          </>
        }
      >
        <label className="block text-sm font-medium mb-1">Next review date</label>
        <input
          type="date"
          min={todayIso}
          value={approveNextDate}
          onChange={(e) => setApproveNextDate(e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"
        />
        <p className="text-xs text-gray-500 mt-1">
          Leave blank to default to today + 365 days.
        </p>
      </Modal>

      {/* Reject modal */}
      <Modal
        open={rejectOpen && !!vendor}
        onClose={() => {
          setRejectOpen(false);
          setRejectReason('');
        }}
        title="Reject Vendor"
        description="Reason is required and will be surfaced on the vendor detail."
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setRejectOpen(false);
                setRejectReason('');
              }}
              className="px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleReject}
              disabled={actionBusy || !rejectReason.trim()}
              className="px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
            >
              Reject
            </button>
          </>
        }
      >
        <textarea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          rows={5}
          maxLength={2000}
          placeholder="Why is this vendor being rejected?"
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"
        />
        <div className="text-xs text-gray-400 text-right">
          {rejectReason.length} / 2000
        </div>
      </Modal>

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}
      {successNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={successNotification.title}
            message={successNotification.message}
            showClose={true}
            onClose={() => setSuccessNotification(null)}
            autoDismiss={3000}
          />
        </NotificationContainer>
      )}
    </div>
  );
}
